import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Users, Target, Lightbulb, BookOpen, ArrowRight, HelpCircle, X } from 'lucide-react';
import './HomePage.css';

const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=1200&q=80&auto=format&fit=crop',
];

export default function HomePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [observerName, setObserverName] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeModal, setActiveModal] = useState(null);

  const definitions = [
    {
      id: 'bbs',
      title: 'WHAT IS BEHAVIOR-BASED SAFETY (BBS)?',
      shortText: 'A proactive safety management approach focusing on observable behaviors.',
      fullText: 'BBS is a proactive safety management approach that focuses on observing, understanding, and positively influencing the safety-critical behaviors of employees. It is grounded in applied behavioral science and recognizes that the majority of workplace incidents are preceded by at-risk behaviors that can be identified and corrected before harm occurs.'
    },
    {
      id: 'vfl',
      title: 'WHAT IS VISIBLE FELT LEADERSHIP (VFL)?',
      shortText: 'Leaders conducting structured, face-to-face safety interactions on the shopfloor.',
      fullText: 'VFL is the practice of managers and leaders conducting structured, face-to-face safety interactions on the shopfloor. A leader\'s physical presence, genuine engagement, and authentic concern for employee safety creates a felt impact that drives a positive safety culture far more effectively than policy documents alone.'
    },
    {
      id: 'safeBehavior',
      title: 'WHAT QUALIFIES AS A SAFE BEHAVIOR?',
      shortText: 'Actions that conform to established safety procedures and best practices.',
      fullText: 'Any observable action that conforms to the established safety procedure, standard, or best practice — e.g., correctly wearing required PPE, following LOTO procedure, maintaining a clean workstation, using correct manual handling technique, adhering to PTW requirements.'
    },
    {
      id: 'atRiskBehavior',
      title: 'WHAT QUALIFIES AS AN AT-RISK BEHAVIOR?',
      shortText: 'Actions that deviate from safe practice and could lead to injury or damage.',
      fullText: 'Any observable action that deviates from an established safe practice and could, under foreseeable circumstances, lead to injury, illness, or property damage. Examples: bypassing a machine guard, lifting incorrectly, walking in a vehicle zone, not wearing required PPE.'
    }
  ];

  useEffect(() => {
    if (slug) {
      // Decode slug to get observer name (slug is formatted as first-last-name)
      const decodedName = slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      setObserverName(decodedName);
    }
  }, [slug]);

  useEffect(() => {
    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % BACKGROUND_IMAGES.length);
    }, 6000); // Change image every 6 seconds

    return () => clearInterval(imageInterval);
  }, []);

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="header-content">
          <div className="logo-section">
            <img src="/logo.png" alt="UFL HSE" className="logo-image" />
          </div>
          <h1>Safety Through Observation</h1>
          <p>Behavior-Based Safety Program Guidance</p>
          {slug && (
            <div className="observer-greeting">
              <p>Welcome, <strong>{observerName}</strong></p>
              <a href={`/submit/${slug}/form`} className="cta-btn primary-btn">
                Start Submitting <ArrowRight size={16} />
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Hero with Illustration */}
      <section className="hero-section">
        <div className="hero-content">
          <h2>What is Behavior-Based Safety?</h2>
          <p>BBS is a proactive safety management approach that focuses on observing, understanding, and positively influencing the safety-critical behaviors of employees.</p>
          {!slug && (
            <a href="#guidance" className="cta-btn">
              Learn More <ArrowRight size={16} />
            </a>
          )}
        </div>
        <div className="hero-illustration">
          <div 
            className="safety-background"
            style={{
              backgroundImage: `linear-gradient(160deg, rgba(10, 30, 16, 0.75) 0%, rgba(26, 71, 42, 0.7) 60%, rgba(10, 25, 14, 0.8) 100%), url('${BACKGROUND_IMAGES[currentImageIndex]}')`
            }}
          ></div>
        </div>
      </section>

      {/* Key Concepts Grid */}
      <section id="guidance" className="concepts-section">
        <div className="section-intro">
          <h2>Observer Guidance, Definitions & BBS Principles</h2>
        </div>
        
        {/* Educational Definitions */}
        <div className="definitions-section">
          <h3>Essential Definitions</h3>
          <div className="definitions-grid">
            {definitions.map(def => (
              <div key={def.id} className="definition-card">
                <h4>{def.title}</h4>
                <p>{def.shortText}</p>
                <button 
                  className="learn-more-btn"
                  onClick={() => setActiveModal(def.id)}
                >
                  <HelpCircle size={16} />
                  <span>Learn More</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Conduct Observation */}
      <section className="observation-section">
        <div className="obs-content">
          <h2>How to Conduct a Quality Observation</h2>
          <p>Follow these 7 steps to conduct effective safety observations:</p>
          
          <ol className="steps-list">
            <li><strong>Observe</strong> a task for at least 2–3 minutes before engaging</li>
            <li><strong>Focus on the behavior,</strong> not the person</li>
            <li><strong>Engage positively</strong> — acknowledge safe acts first</li>
            <li><strong>Discuss at-risk behaviors</strong> objectively and collaboratively</li>
            <li><strong>Agree on corrective actions</strong> together</li>
            <li><strong>Thank the employee</strong> for their time and cooperation</li>
            <li><strong>Complete the register</strong> promptly after interaction</li>
          </ol>
        </div>
      </section>

      {/* Severity Guide */}
      <section className="severity-section">
        <h2>Behavior Severity Guide</h2>
        
        <div className="severity-showcase">
          <div className="severity-item">
            <div className="severity-level low">LOW</div>
            <p>Minor deviation; no immediate injury potential.</p>
          </div>
          <div className="severity-item">
            <div className="severity-level medium">MEDIUM</div>
            <p>Moderate risk; potential for injury.</p>
          </div>
          <div className="severity-item">
            <div className="severity-level high">HIGH</div>
            <p>Significant injury potential. Stop and investigate.</p>
          </div>
          <div className="severity-item">
            <div className="severity-level critical">CRITICAL</div>
            <p>Life-threatening risk. Stop immediately, escalate.</p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="principles-section">
        <h2>Core Principles</h2>
        
        <div className="principles-grid">
          <div className="principle">
            <div className="principle-icon">
              <Target size={24} />
            </div>
            <h3>Focus on Behavior</h3>
            <p>Observe actions, not individuals. Keep feedback objective and constructive.</p>
          </div>
          
          <div className="principle">
            <div className="principle-icon">
              <Lightbulb size={24} />
            </div>
            <h3>Build Culture</h3>
            <p>Create a blame-free, positive safety culture through consistent engagement.</p>
          </div>

          <div className="principle">
            <div className="principle-icon">
              <BookOpen size={24} />
            </div>
            <h3>Use Data</h3>
            <p>Track observations for trend analysis and continuous improvement.</p>
          </div>

          <div className="principle">
            <div className="principle-icon">
              <Users size={24} />
            </div>
            <h3>Collaborate</h3>
            <p>Work together with employees to identify and agree on corrective actions.</p>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="cta-footer">
        <h2>Ready to Submit an Observation?</h2>
        <p>Use your personalized form link to record safety observations</p>
        {slug ? (
          <a href={`/submit/${slug}/form`} className="cta-btn-large">
            Go to Form <ArrowRight size={16} />
          </a>
        ) : (
          <a href="/home" className="cta-btn-large">
            Get Started
          </a>
        )}
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <p>&copy; 2026 UFL HSE | Behavior-Based Safety Program</p>
      </footer>

      {/* Educational Modal */}
      {activeModal && (
        <div className="definition-modal-backdrop" onClick={() => setActiveModal(null)}>
          <div className="definition-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{definitions.find(d => d.id === activeModal)?.title}</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setActiveModal(null)}
                title="Close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>{definitions.find(d => d.id === activeModal)?.fullText}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
